<template>
    <div class="checkbox-list mt-4">
        <template v-if="membersOptions.length">
            <b-form-checkbox
                @change="onSelectAll"
                :checked="isAllSelected"
                :disabled="'company' === assignTo && 'Company' === currentUser.role"
                class="text-capitalize"
                >All Members</b-form-checkbox
            >

            <b-form-checkbox
                v-for="memberOption in membersOptions"
                v-model="innerValue"
                :value="memberOption.value"
                :key="`member-${memberOption.value}`"
            >
                {{ memberOption.text }}

                <template v-if="includeReports">
                    <ul v-if="reportsOptions.length" class="my-2 pl-3 small">
                        <li
                            v-for="reportOption in reportsOptions"
                            :key="`report-${reportOption.value}`"
                            :class="{
                                'text-gray': memberOption.reports.includes(
                                    reportOption.value
                                ),
                            }"
                            class="text-info"
                        >
                            <s v-if="memberOption.reports.includes(reportOption.value)">
                                {{ reportOption.text }}
                            </s>
                            <template v-else>
                                {{ reportOption.text }}
                            </template>
                        </li>
                    </ul>
                </template>
            </b-form-checkbox>
        </template>
        <p v-else class="small">No members available.</p>
    </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from "@vue/composition-api";
import Fuse from "fuse.js";

import Members from "@services/Members";
import Reports from "@services/Reports";
import { useAuth } from "@composables/auth";

export default defineComponent({
    name: "MembersCheckboxList",
    emits: ["input"],
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        assignTo: String,
        companyId: Number,
        includeReports: Boolean,
        searchKeyword: String,
    },
    setup(props, { emit }) {
        const { currentUser, userRole } = useAuth();

        const membersList = ref([]);
        const reportsList = ref([]);

        const innerValue = computed({
            get: () => props.value,
            set: (newValue) => emit("input", newValue),
        });

        const membersOptions = computed(() => {
            let collection = membersList.value.map((item) => ({
                value: item.id,
                text: item.user.first_name,
                reports: item.reports.map((item) => item.report_id),
            }));

            if (props.searchKeyword) {
                const fuse = new Fuse(collection, {
                    keys: ["text"],
                    includeScore: true,
                    useExtendedSearch: true,
                    threshold: 0.3,
                });

                collection = fuse.search(props.searchKeyword).map((item) => item.item);
            }

            return collection;
        });

        const reportsOptions = computed(() =>
            reportsList.value.map((item) => ({
                value: item.report_id || item.id,
                text: currentUser.value.is_superuser
                    ? item.name
                    : item.display_name || item.name,
            }))
        );

        const isAllSelected = computed(
            () => membersOptions.value.length == props.value.length
        );

        const reportAssignee = computed(() => {
            let id = currentUser.value.member_id;

            if ("Corporate Admin" === userRole.value)
                id = currentUser.value.company.id || null;

            return {
                id: id,
                type: userRole.value,
            };
        });

        const getMembers = async () => {
            const resp = await Members.getMembers(
                {
                    page_size: 1000,
                },
                props.companyId
            );

            membersList.value = resp.data.data.data.members.results;
        };

        const getReports = async () => {
            const resp = await Reports.getReports(
                reportAssignee.value.id,
                reportAssignee.value.type
            );

            reportsList.value = resp.data.data.data;
        };

        const onSelectAll = () => {
            const emitValue = isAllSelected.value
                ? []
                : membersOptions.value.map((item) => item.value);

            emit("input", emitValue);
        };

        onMounted(() => {
            getMembers();

            if (props.includeReports) getReports();
        });

        return {
            membersOptions,
            reportsOptions,
            innerValue,
            isAllSelected,
            onSelectAll,
        };
    },
});
</script>

<style lang="scss">
.checkbox-list {
    max-height: 190px;
    overflow-y: auto;

    .custom-control {
        margin-bottom: 0.5rem;
    }
}
</style>
